"use client"

import * as React from "react"
import { Input as InputPrimitive } from "@base-ui/react/input"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const inputWrapperVariants = cva(
  "relative inline-flex w-full text-base/5 transition-shadow has-disabled:opacity-64 has-aria-invalid:border-destructive/36 has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 sm:text-sm",
  {
    variants: {
      variant: {
        default:
          "border border-input bg-background bg-clip-padding shadow-xs ring-ring/24 before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] has-focus-visible:border-ring has-focus-visible:ring-[3px] has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none dark:bg-input/32 dark:not-in-data-[slot=group]:bg-clip-border dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/8%)] dark:has-aria-invalid:ring-destructive/24",
        outline:
          "border-2 border-input bg-transparent ring-ring/24 has-focus-visible:border-ring has-focus-visible:ring-[3px] has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none dark:has-aria-invalid:ring-destructive/24",
        muted:
          "border border-transparent bg-muted has-focus-visible:border-ring has-focus-visible:ring-[3px] ring-ring/24 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none dark:bg-input/16 dark:has-aria-invalid:ring-destructive/24",
      },
      rounded: {
        default: "rounded-lg",
        full: "rounded-full",
        none: "rounded-none",
        sm: "rounded-md",
        xl: "rounded-xl",
      },
      size: {
        sm: "",
        default: "",
        lg: "",
      },
    },
    defaultVariants: {
      variant: "default",
      rounded: "default",
      size: "default",
    },
  }
)

const inputInnerVariants = cva(
  "w-full min-w-0 rounded-[inherit] outline-none placeholder:text-muted-foreground/64",
  {
    variants: {
      size: {
        sm: "px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(1)-1px)]",
        default: "px-[calc(--spacing(3)-1px)] py-[calc(--spacing(1.5)-1px)]",
        lg: "px-[calc(--spacing(3)-1px)] py-[calc(--spacing(2)-1px)]",
      },
      iconStart: {
        sm: "pl-[calc(--spacing(7)-1px)]",
        default: "pl-[calc(--spacing(8)-1px)]",
        lg: "pl-[calc(--spacing(9)-1px)]",
        none: "",
      },
      iconEnd: {
        sm: "pr-[calc(--spacing(7)-1px)]",
        default: "pr-[calc(--spacing(8)-1px)]",
        lg: "pr-[calc(--spacing(9)-1px)]",
        none: "",
      },
    },
    defaultVariants: {
      size: "default",
      iconStart: "none",
      iconEnd: "none",
    },
  }
)

const iconWrapperVariants = cva(
  "pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground/64 [&_svg]:shrink-0",
  {
    variants: {
      side: {
        start: "left-0",
        end: "right-0",
      },
      size: {
        sm: "px-2 [&_svg]:size-3.5",
        default: "px-2.5 [&_svg]:size-4",
        lg: "px-3 [&_svg]:size-4.5",
      },
    },
    defaultVariants: {
      side: "start",
      size: "default",
    },
  }
)

type InputProps = Omit<
  InputPrimitive.Props & React.RefAttributes<HTMLInputElement>,
  "size"
> &
  Omit<VariantProps<typeof inputWrapperVariants>, "size"> & {
    size?: "sm" | "default" | "lg" | number
    unstyled?: boolean
    startIcon?: React.ReactNode
    endIcon?: React.ReactNode
  }

function Input({
  className,
  size = "default",
  variant,
  rounded,
  unstyled = false,
  startIcon,
  endIcon,
  ...props
}: InputProps) {
  const sizeVariant =
    typeof size === "string" ? (size as "sm" | "default" | "lg") : "default"

  return (
    <span
      data-slot="input-control"
      data-size={size}
      className={
        cn(
          !unstyled && inputWrapperVariants({ variant, rounded, size: sizeVariant }),
          className
        ) || undefined
      }
    >
      {startIcon && (
        <span className={iconWrapperVariants({ side: "start", size: sizeVariant })}>
          {startIcon}
        </span>
      )}
      <InputPrimitive
        data-slot="input"
        className={cn(
          !unstyled &&
            inputInnerVariants({
              size: sizeVariant,
              iconStart: startIcon ? sizeVariant : "none",
              iconEnd: endIcon ? sizeVariant : "none",
            }),
          props.type === "search" &&
            "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
          props.type === "file" &&
            "text-muted-foreground file:me-3 file:bg-transparent file:text-sm file:font-medium file:text-foreground"
        )}
        size={typeof size === "number" ? size : undefined}
        {...props}
      />
      {endIcon && (
        <span className={iconWrapperVariants({ side: "end", size: sizeVariant })}>
          {endIcon}
        </span>
      )}
    </span>
  )
}

export { Input, inputWrapperVariants, inputInnerVariants, iconWrapperVariants, type InputProps }
